import { ConfirmDialog } from 'components/confirm-dialog/confirm-dialog'
import { useAuth0 } from '@auth0/auth0-react'
import { UserIdentityDTO } from 'api-models/src'
import {
	AppButton,
	Dropdown,
	Modal,
	ModalHeader,
	Tooltip
} from '@clickguard/design-system'
import { Lock01 } from 'components/icons'
import { useStores } from 'hooks'
import { ReactElement, useEffect, useState } from 'react'
import {
	RiFacebookLine,
	RiGoogleLine,
	RiLinkedinLine
} from 'react-icons/ri'
import { FaRegTrashAlt } from 'react-icons/fa'
import { toast } from 'react-toastify'

interface loginMethodCRowsProps {
	item: UserIdentityDTO
	showDelete: boolean
	loading: boolean
}

export const LoginMethodRows = (props: loginMethodCRowsProps) => {
	const { item, showDelete } = props
	const { userStore } = useStores()
	const { deleteLoginMethod, passwordReset, changeMainEmail, user } = userStore
	const { logout } = useAuth0()

	const [emailValue, setEmailValue] = useState<string>(item.email as string)
	const [verifiedEmails, setVerifiedEmails] = useState<
		{ value: string; label: string }[]
	>([])
	const [changeEmailOpen, setChangeEmailOpen] = useState(false)
	const [confirmChangeOpen, setConfirmChangeOpen] = useState(false)
	const [resetOpen, setResetOpen] = useState(false)
	const [deleteOpen, setDeleteOpen] = useState(false)

	useEffect(() => {
		const verified = user?.email_addresses
			.filter((email) => email.email_verified)
			.map((email) => ({ label: email.email, value: email.email }))
		setVerifiedEmails(verified ?? [])
	}, [user?.email_addresses])

	const handlePasswordChange = async () => {
		try {
			await passwordReset(user!.email)
			toast.success('Password reset email sent')
			setTimeout(() => {
				logout()
			}, 500)
		} catch (err) {
			toast.error('Failed to send password reset email')
		}
	}

	const handleDelete = async () => {
		try {
			await deleteLoginMethod(item.id, item.provider)
			toast.success('Login method removed')
		} catch (err) {
			toast.error('Failed to remove login method')
		}
	}

	const handleConfirmEmailChange = async () => {
		try {
			await changeMainEmail({ email: emailValue })
			logout()
		} catch (error) {
			//
		}
	}

	const renderLoginTypeName = (connection: string): string | undefined => {
		if (connection === 'google-oauth2') return 'Google Account'
		if (connection === 'facebook') return 'Facebook Account'
		if (connection === 'linkedin') return 'LinkedIn Account'
		if (
			connection === 'Username-Password-Authentication' ||
			connection === 'stage-database'
		)
			return 'Email and Password'
	}

	const renderSocialIcons = (
		isSocial: boolean,
		connection: string
	): ReactElement => {
		if (isSocial) {
			if (connection === 'google-oauth2')
				return <RiGoogleLine className="h-6 w-6 text-[color:var(--text-secondary)]" />
			if (connection === 'facebook')
				return <RiFacebookLine className="h-6 w-6 text-[color:var(--text-secondary)]" />
			if (connection === 'linkedin')
				return <RiLinkedinLine className="h-6 w-6 text-[color:var(--text-secondary)]" />
		}
		return <Lock01 className="h-6 w-6 text-[color:var(--text-secondary)]" />
	}

	return (
		<div className="flex items-center justify-between gap-4 rounded-xl border border-t-checkbox p-[13px]">
			<div className="flex min-w-0 flex-1 items-center gap-4">
				<span className="shrink-0">
					{renderSocialIcons(item.is_social, item.connection)}
				</span>
				<div className="flex min-w-0 flex-col gap-0.5">
					<p className="text-[16px] leading-[22px] text-[color:var(--text-primary)]">
						{renderLoginTypeName(item.connection)}
					</p>
					<p className="max-w-[220px] truncate text-[14px] leading-[18px] text-[color:var(--text-secondary)]">
						{item.email}
					</p>
				</div>
			</div>
			<div className="flex shrink-0 items-center gap-3">
				{!item.is_social && !showDelete && (
					<AppButton
						variant="secondary"
						size="md"
						label="Change email"
						className="!border-t-card-border !text-t-base-blue"
						onClick={() => setChangeEmailOpen(true)}
					/>
				)}
				{!item.is_social && (
					<AppButton
						variant="secondary"
						size="md"
						label="Reset password"
						className="!border-t-card-border !text-t-base-blue"
						onClick={() => setResetOpen(true)}
					/>
				)}
				{showDelete && (
					<Tooltip content="Remove login method">
						<AppButton
							variant="destructive"
							size="md"
							hideLabel
							label="Remove login method"
							leftIcon={<FaRegTrashAlt className="h-4 w-4" />}
							onClick={() => setDeleteOpen(true)}
						/>
					</Tooltip>
				)}
			</div>

			{/* Change email — pick a verified address */}
			<Modal
				open={changeEmailOpen}
				onClose={() => setChangeEmailOpen(false)}
				size="md"
				rounded="modal"
				className="p-6"
				overlayClassName="backdrop-blur-sm"
			>
				<div className="flex flex-col gap-6">
					<ModalHeader
						size="sm"
						titleColor="navy"
						title="Change email address"
						subtitle="Select a verified email address to use for password login. After changing the login email address, you will be logged out and prompted to log in again."
						onClose={() => setChangeEmailOpen(false)}
					/>
					<div className="flex flex-col gap-1.5">
						<p className="text-[14px] leading-[18px] font-medium text-t-base-blue">
							Email address
						</p>
						<Dropdown
							className="w-full [&>button]:w-full [&>button]:!justify-between [&>button]:!font-normal"
							menuWidth="trigger"
							placeholder="Select email address"
							value={emailValue}
							options={verifiedEmails}
							onValueChange={(v) => setEmailValue(v)}
						/>
					</div>
					<div className="flex justify-end gap-3">
						<AppButton
							variant="secondary"
							size="md"
							label="Cancel"
							onClick={() => setChangeEmailOpen(false)}
						/>
						<AppButton
							variant="primary"
							size="md"
							label="Confirm"
							onClick={() => {
								setChangeEmailOpen(false)
								setConfirmChangeOpen(true)
							}}
						/>
					</div>
				</div>
			</Modal>

			{/* Changing email — final confirmation */}
			<ConfirmDialog
				open={confirmChangeOpen}
				onClose={() => setConfirmChangeOpen(false)}
				onConfirm={handleConfirmEmailChange}
				title="Changing email address"
				description={`You are about to change your login email address to ${emailValue}. You will be logged out and you’ll need to log in again. Proceed?`}
				confirmLabel="Proceed"
				loading={props.loading}
			/>

			{/* Reset password */}
			<ConfirmDialog
				open={resetOpen}
				onClose={() => setResetOpen(false)}
				onConfirm={handlePasswordChange}
				title="Reset password"
				description="This action will start a password reset process. You will receive instructions for setting a new password in your inbox. Proceed?"
				confirmLabel="Reset password"
			/>

			{/* Remove login method */}
			<ConfirmDialog
				open={deleteOpen}
				onClose={() => setDeleteOpen(false)}
				onConfirm={handleDelete}
				tone="danger"
				title="Remove login method"
				description="Are you sure you want to remove this login method? You will be logged out and prompted to log in again. Proceed?"
				confirmLabel="Remove"
			/>
		</div>
	)
}
